import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  deleteDoc, 
  updateDoc, 
  query, 
  where,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '../firebase';

// Interface pour les notes
export interface Note {
  id?: string;
  nom: string;
  contenu: string;
  dossierId: string | null;
  bienId: string;
  userId?: string;
  type: 'note';
  categorie: 'gestioncourante' | 'travaux' | 'historique';
  createdAt: Date;
  updatedAt?: Date;
  tags?: string[];
}

const NOTES_COLLECTION = 'notes';

class NotesService {
  
  /**
   * Créer une nouvelle note
   */
  async creerNote(noteData: Omit<Note, 'id' | 'userId' | 'createdAt' | 'updatedAt' | 'type'>): Promise<string> {
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    const note = {
      ...noteData,
      userId: user.uid,
      type: 'note' as const,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, NOTES_COLLECTION), note);
    return docRef.id;
  }

  /**
   * Récupérer toutes les notes d'un bien pour l'utilisateur connecté
   */
  async getNotesByBien(bienId: string, dossierId?: string | null, categorie?: 'gestioncourante' | 'travaux' | 'historique'): Promise<Note[]> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    let q;
    if (dossierId === undefined) {
      // Récupérer toutes les notes du bien avec filtrage par catégorie
      if (categorie) {
        q = query(
          collection(db, NOTES_COLLECTION),
          where('userId', '==', user.uid),
          where('bienId', '==', bienId),
          where('categorie', '==', categorie)
        );
      } else {
        q = query(
          collection(db, NOTES_COLLECTION),
          where('userId', '==', user.uid),
          where('bienId', '==', bienId)
        );
      }
    } else {
      // Récupérer les notes d'un dossier spécifique
      if (categorie) {
        q = query(
          collection(db, NOTES_COLLECTION),
          where('userId', '==', user.uid),
          where('bienId', '==', bienId),
          where('dossierId', '==', dossierId),
          where('categorie', '==', categorie)
        );
      } else {
        q = query(
          collection(db, NOTES_COLLECTION),
          where('userId', '==', user.uid),
          where('bienId', '==', bienId),
          where('dossierId', '==', dossierId)
        );
      }
    }

    const querySnapshot = await getDocs(q);
    const notes: Note[] = [];

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      notes.push({
        id: docSnapshot.id,
        nom: data.nom,
        contenu: data.contenu,
        dossierId: data.dossierId,
        bienId: data.bienId,
        userId: data.userId,
        type: 'note',
        categorie: data.categorie,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate(),
        tags: data.tags,
      });
    });

    // Trier par date de création (plus récentes en premier)
    notes.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    return notes;
  }

  /**
   * Modifier une note
   */
  async modifierNote(noteId: string, updates: Partial<Pick<Note, 'nom' | 'contenu' | 'tags'>>): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    const noteRef = doc(db, NOTES_COLLECTION, noteId);
    await updateDoc(noteRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
  }

  /**
   * Supprimer une note
   */
  async supprimerNote(noteId: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    const noteRef = doc(db, NOTES_COLLECTION, noteId);
    await deleteDoc(noteRef);
  }
}

export const notesService = new NotesService();
export default notesService;
